import React, { useState, useEffect } from 'react';
import { Grid } from '@mui/material';
import { ImportServicesTypography, ImportServicesSection } from './ImportService.styles';
import { PageContainer } from '../../../components/atoms/PageContainer';
import { TypographyVariantProps } from '../../../components/atoms/Typography';
import { UploadButton } from '../../../components/molecules/UploadButton';
import { ImportServiceTable } from './ImportServiceTable';
import { downloadFile } from '../../../utils/file-utils';
import { useGetUploadedCsvFile } from '../../../queries/uploadedfiles-query';
import { IMPORT_FILE_MIME_TYPES, REACT_APP_SERVICE_IMPORT_EXCEL_KEY } from '../../../configs/envs';
import { UploadedCsvFile } from '../../../@types/uploaded-files.type';
import { useUploadResource } from '../../../queries/import-query';
import { ImportResourceResponse } from '../../../@types';
import { UploadResourceType, ImportResourceTypeCode } from '../../../configs/enums';
import { Button, ButtonVariantProps } from '../../../components/atoms/Button';
import { ImportData } from './ImportServiceTable.props';
import { SERVICE_IMPORT_DATA } from '../../../constants/service.constant';
import { setImportSessionData, getImportSessionData, clearImportSessionData } from '../../../utils/imports';
import { TabHandler } from '../../../handlers/TabHandler';
import { PLATFORM_NAME } from '../../../configs/common';

export const ImportService: React.FC = () => {
  window.addEventListener('beforeunload', function() {
    clearImportSessionData(ImportResourceTypeCode.Service);
  });

  const importSessionData = getImportSessionData(ImportResourceTypeCode.Service);

  const [fileID, setFileID] = useState<number>(0);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedCsvFile>();
  const [importData, setImportData] = useState<ImportData>(SERVICE_IMPORT_DATA);
  
  const uploadedFilesQuery = useGetUploadedCsvFile(REACT_APP_SERVICE_IMPORT_EXCEL_KEY);
  const uploadResourceQuery = useUploadResource();

  TabHandler(PLATFORM_NAME);

  useEffect(() => {
    !importSessionData?.isImportComplete ?
      setImportData(prev => ({
        ...prev,
        fileName: importSessionData?.fileName || '',
        fileSize: importSessionData?.fileSize || 0  
      }))
      : 
      clearImportSessionData(ImportResourceTypeCode.Service);
    uploadedFilesQuery.data && setUploadedFiles(uploadedFilesQuery.data);
  }, [uploadedFiles]);

  const handleClick = async ()=> {
    const refetchUploadedFilesQuery = await uploadedFilesQuery.refetch();
    if(refetchUploadedFilesQuery.data){
      setUploadedFiles(refetchUploadedFilesQuery.data);
      downloadFile(refetchUploadedFilesQuery.data?.url , 'service-import-template');
    } 
  };

  const handleUpload = async (fileList: FileList)=> {
    setFileID(0);
    const uploadedFile: ImportResourceResponse = await uploadResourceQuery.mutateAsync({
      type: UploadResourceType.Services,
      file: fileList[0]
    }) as ImportResourceResponse;
    setFileID(uploadedFile.id);
  };

  const onChangeFileData = async (value: any)=> {
    setImportData(prev => ({ ...prev, fileName: value.fileName, fileSize: value.fileSize }));
    setImportSessionData(
      ImportResourceTypeCode.Service,
      {
        fileName: value.fileName,
        fileSize: value.fileSize
      }
    );
  };

  return (
    <PageContainer>
      <ImportServicesTypography variant={TypographyVariantProps.H5} fontWeight={600}>
        Import Services
      </ImportServicesTypography>
      <Grid mb={2} mt={2} size={{ xs: 9 }}>
        <Button variant={ButtonVariantProps.Text} onClick={handleClick}>
          Download Excel template
        </Button>
      </Grid>
      <Grid size={{ xs: 9 }}>
        <UploadButton
          showName
          fileTypes={IMPORT_FILE_MIME_TYPES.split(',')}
          maxFileSize={1024}
          setFileDetails={handleUpload}
          value={ { fileName: importData?.fileName, fileSize: importData?.fileSize } }
          onChangeFileData={onChangeFileData}
        />
      </Grid>
      <ImportServicesSection>
        <ImportServiceTable fileId={fileID}/>
      </ImportServicesSection>
    </PageContainer>
  );
};